import React, { useEffect, useState, useMemo } from 'react';
import { Accordion, Button, Card, Stack, useAccordionButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import pluralize from 'pluralize';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';
import { compact, flatten } from 'lodash';

import { Collaboration } from '@models/Collaboration';
import { formatDateTime } from '@utils/datetime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConfirmModal } from '@components/Base';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

import {
  collaborationsFetch,
  CollaborationsFetchPayload,
  collaborationCreate,
  CollaborationCreatePayload,
  collaborationDelete,
  CollaborationDeletePayload,
  stateClear,
  StateUpdatePayload,
  stateUpdate,
} from './action';
import { selectFeatureCollaboration } from './selector';
import { CollaborationFormModal, CollaborationFormValues } from './collaboration-form-modal';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = Pick<CollaborationsFetchPayload, 'collaboratableType' | 'collaboratableId'> & {
  canCreate?: boolean;
  canDelete?: boolean;
};

const CollaborationCardBody = ({ collaboratableType, collaboratableId, canCreate = false, canDelete = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { collaborations, isLoading, isReload, isSuccess, pagination } = useSelector(selectFeatureCollaboration);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedCollaboration, setSelectedCollaboration] = useState<Collaboration | null>(null);
  const [showCollaborationFormModal, setShowCollaborationFormModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const columns = useMemo(() => flatten(compact([
    canDelete && {
      cell: (collaboration) => (
        <Stack direction="horizontal" gap={1}>
          <Button onClick={() => handleDelete(collaboration)} variant="outline-primary" size="sm">
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Stack>
      ),
      button: 'true',
      center: 'true',
    },
    {
      name: t('common:createdDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      minWidth: '180px',
    },
    {
      name: "Collaboration",
      selector: ({ collaborator }) => collaborator.name,
    },
  ])) as TableColumn<Collaboration>[], [canDelete]);

  const handleDelete = (collaboration: Collaboration) => {
    setSelectedCollaboration(collaboration);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    setShowCollaborationFormModal(false);
    const payload: StateUpdatePayload = { isSuccess: false, serverErrors: {} };
    dispatch(stateUpdate(payload));
  };

  useEffect(() => {
    if (isSuccess && showCollaborationFormModal) {
      handleClose();
    };
  }, [isSuccess]);

  useEffect(() => {
    const payload: CollaborationsFetchPayload = { collaboratableType, collaboratableId, page, perPage };
    dispatch(collaborationsFetch(payload));
  }, [collaboratableType, collaboratableId, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  useUpdateEffect(() => {
    const payload: CollaborationsFetchPayload = { collaboratableType, collaboratableId, page: 1, perPage };
    dispatch(collaborationsFetch(payload));
  }, [isReload]);

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              {pluralize("Collaboration", 2)} ({pagination?.total ?? 0})
            </Toggle>
            {canCreate && (
              <Button onClick={() => setShowCollaborationFormModal(true)} variant="outline-primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {t('common:add')}
              </Button>
            )}
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <DataTable
              columns={columns}
              data={collaborations ?? []}
              customStyles={customStyles}
              fixedHeader
              pagination
              paginationServer
              progressPending={isLoading}
              paginationPerPage={perPage}
              paginationTotalRows={pagination?.total ?? 0}
              onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
              onChangePage={(newPage) => setPage(newPage)}
            />
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <CollaborationFormModal
        show={showCollaborationFormModal}
        onHide={handleClose}
        onSubmit={(values: CollaborationFormValues) => {
          const payload: CollaborationCreatePayload = { collaboratableType, collaboratableId, ...values };
          dispatch(collaborationCreate(payload));
        }}
      />

      {canDelete && (
        <ConfirmModal
          show={showConfirmModal}
          onHide={() => {
            setShowConfirmModal(false);
            setSelectedCollaboration(null);
          }}
          onConfirm={() => {
            if (selectedCollaboration) {
              const payload: CollaborationDeletePayload = { id: selectedCollaboration.id };
              dispatch(collaborationDelete(payload));
              setShowConfirmModal(false);
              setSelectedCollaboration(null);
            }
          }}
        />
      )}
    </>
  );
};

export { CollaborationCardBody };
